import { Plugin } from '@ckeditor/ckeditor5-core';
import { ButtonView, createDropdown, addListToDropdown } from '@ckeditor/ckeditor5-ui';
import { Collection } from '@ckeditor/ckeditor5-utils';
import imageConfig from './image-config';

export default class ImageBrowserUI extends Plugin {
    static get pluginName() {
        return 'ImageBrowserUI';
    }

    init() {
        const editor = this.editor;
        const t = editor.t;

        // Ajouter le bouton au toolbar
        editor.ui.componentFactory.add('imageBrowser', () => {
            const dropdown = createDropdown(editor.locale);
            
            // Configuration du bouton principal
            dropdown.buttonView.set({
                label: t('Parcourir les images'),
                icon: '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M6.972 16.615a.997.997 0 0 1-.744-.292l-4.596-4.596a1 1 0 0 1 0-1.414l4.596-4.596a1 1 0 0 1 1.414 0l4.596 4.596a.999.999 0 0 1 0 1.414l-4.596 4.596a.997.997 0 0 1-.67.292zm-3.182-5.595L7.387 14.6l3.597-3.585-3.597-3.585-3.597 3.585z"/><path d="M14.657 16.615a.997.997 0 0 1-.744-.292l-4.596-4.596a.999.999 0 0 1 0-1.414l4.596-4.596a1 1 0 0 1 1.414 0l4.596 4.596a.999.999 0 0 1 0 1.414l-4.596 4.596a.997.997 0 0 1-.67.292zm-3.182-5.595L15.072 14.6l3.597-3.585-3.597-3.585-3.597 3.585z"/></svg>',
                tooltip: true
            });

            // Images prédéfinies - vous pouvez les personnaliser
            const predefinedImages = this._getPredefinedImages();

            // Créer la collection d'items pour le dropdown de façon plus simple
            const items = new Collection();
            
            predefinedImages.forEach((imageData, index) => {
                items.add({
                    type: 'button',
                    model: {
                        id: `image-item-${index}`,
                        label: imageData.title,
                        withText: true,
                        execute: () => {
                            this._insertImage(imageData);
                            dropdown.isOpen = false;
                        }
                    }
                });
            });

            // Ajouter la liste au dropdown
            addListToDropdown(dropdown, items);
            
            // Ajouter la classe CSS au panel
            dropdown.panelView.extendTemplate({
                attributes: {
                    class: ['ck-image-browser-panel']
                }
            });

            // Ajouter les styles aux items après le rendu
            dropdown.on('change:isOpen', () => {
                if (dropdown.isOpen) {
                    // Appliquer les styles avec un petit délai pour s'assurer que le DOM est prêt
                    setTimeout(() => {
                        this._applyImageStyles(dropdown, predefinedImages);
                    }, 10);
                }
            });

            return dropdown;
        });
    }

    /**
     * Applique les styles d'images aux items du dropdown après rendu
     */
    _applyImageStyles(dropdown, predefinedImages) {
        const panelElement = dropdown.panelView.element;
        if (!panelElement) return;

        const listItems = panelElement.querySelectorAll('.ck-list__item .ck-button');
        
        listItems.forEach((item, index) => {
            if (index < predefinedImages.length) {
                const imageData = predefinedImages[index];
                
                // Ajouter la classe et les styles d'image
                item.classList.add('ck-image-browser-item');
                item.style.backgroundImage = `url('${imageData.src}')`;
                item.style.backgroundSize = '50px 40px';
                item.style.backgroundRepeat = 'no-repeat';
                item.style.backgroundPosition = '10px center';
                item.style.paddingLeft = '65px';
                item.style.minHeight = '60px';
                item.style.display = 'flex';
                item.style.alignItems = 'center';
                
                // Ajouter le titre de l'image
                item.title = imageData.alt || imageData.title;
            }
        });
    }

    /**
     * Définit les images prédéfinies - vous pouvez modifier cette méthode
     * pour charger les images depuis une API ou une base de données
     */
    _getPredefinedImages() {
        // Utiliser la configuration externe
        const editorConfig = this.editor.config.get('imageBrowser');
        
        // Si une configuration spécifique est fournie dans l'éditeur, l'utiliser
        if (editorConfig && editorConfig.predefinedImages) {
            return editorConfig.predefinedImages;
        }
        
        // Sinon, utiliser la configuration par défaut du fichier image-config.js
        return imageConfig.predefinedImages || [
            // Images de fallback si aucune configuration n'est trouvée
            {
                src: 'https://via.placeholder.com/300x200/CCCCCC/FFFFFF?text=Aucune+Image',
                title: 'Aucune image configurée',
                alt: 'Veuillez configurer vos images dans image-config.js',
                width: 300,
                height: 200
            }
        ];
    }

    /**
     * Insère l'image sélectionnée dans l'éditeur
     */
    _insertImage(imageData) {
        const editor = this.editor;
        const model = editor.model;

        model.change(writer => {
            // Créer l'élément image
            const imageElement = writer.createElement('imageBlock', {
                src: imageData.src,
                alt: imageData.alt || imageData.title
            });

            // Si des dimensions sont spécifiées, les ajouter
            if (imageData.width) {
                writer.setAttribute('width', `${imageData.width}px`, imageElement);
            }
            if (imageData.height) {
                writer.setAttribute('height', `${imageData.height}px`, imageElement);
            }

            // Insérer l'image à la position actuelle du curseur
            model.insertContent(imageElement);
        });
    }
}
